import * as React from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, LinearProgress, MenuItem, Select, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
    completeUserCampaignMission, getUserCampaign, reopenUserCampaignMission, resetUserCampaignMission,
    setUserCampaignGoalProgress,
} from '../Api';
import UserCampaignAdminDto, { AdminUserMissionDto, AdminUserMissionGoalDto } from '../models/dtos/UserCampaignAdminDto';

type PendingAction =
    | { kind: 'complete'; mission: AdminUserMissionDto; stars: number }
    | { kind: 'reset'; mission: AdminUserMissionDto }
    | { kind: 'reopen'; mission: AdminUserMissionDto }
    | { kind: 'progress'; mission: AdminUserMissionDto; goal: AdminUserMissionGoalDto; value: number }
    | { kind: 'goalDone'; mission: AdminUserMissionDto; goal: AdminUserMissionGoalDto }
    | null;

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '—';
}

function formatDuration(from?: string | null, to?: string | null) {
    if (!from || !to) return '—';
    const seconds = Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 1000));
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function formatWithin(seconds: number | null) {
    if (seconds == null) return 'no limit';
    if (seconds % 86400 === 0) return `${seconds / 86400} days`;
    return `${Math.round(seconds / 3600)} hours`;
}

function Stars({ value, size = 18 }: { value: number | null | undefined; size?: number }) {
    const stars = value ?? 0;
    return (
        <Stack direction="row" alignItems="center">
            {[1, 2, 3].map(i => i <= stars
                ? <StarIcon key={i} sx={{ fontSize: size, color: '#d4a017' }} />
                : <StarBorderIcon key={i} sx={{ fontSize: size, color: '#9aa4ad' }} />)}
        </Stack>
    );
}

function stateChip(state: string) {
    switch (state) {
        case 'COMPLETED': return <Chip size="small" color="success" label="Completed" />;
        case 'ACTIVE': return <Chip size="small" color="info" label="Active" />;
        default: return <Chip size="small" label="Not reached" />;
    }
}

function taskChip(goal: AdminUserMissionGoalDto) {
    if (goal.user_task_id == null) return <Chip size="small" variant="outlined" label="no task" />;
    const status = goal.status ?? '?';
    const color = status === 'COMPLETED' ? 'success' : status === 'IN_PROGRESS' ? 'info' : status === 'ASSIGNED' ? 'default' : 'error';
    return <Chip size="small" color={color} label={status} />;
}

export default function UserMissionsTable({ userId }: { userId: number }) {
    const [data, setData] = React.useState<UserCampaignAdminDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [pending, setPending] = React.useState<PendingAction>(null);
    const [completeStars, setCompleteStars] = React.useState(3);
    /** Draft progress values per goal id while the admin types. */
    const [drafts, setDrafts] = React.useState<Record<number, string>>({});
    const [collapsed, setCollapsed] = React.useState<Set<number>>(new Set());

    const load = React.useCallback(async () => {
        setLoading(true);
        const result = await getUserCampaign(userId);
        setLoading(false);
        if (result == null) {
            setError('Failed to load the campaign.');
            return;
        }
        setError(null);
        setData(result);
        // Completed chapters fold by default; the active one and the next stay open.
        setCollapsed(new Set(result.missions.filter(m => m.state === 'COMPLETED').map(m => m.mission_id)));
    }, [userId]);

    React.useEffect(() => { load(); }, [load]);

    const runAction = async () => {
        if (pending == null) return;
        const action = pending;
        setPending(null);
        setBusy(true);
        setNotice(null);
        let result: { data: UserCampaignAdminDto | null; error: string | null };
        let done: string;
        switch (action.kind) {
            case 'complete':
                result = await completeUserCampaignMission(userId, action.stars);
                done = `Chapter ${action.mission.number} completed with ${action.stars} star(s); the scaled reward was paid.`;
                break;
            case 'reset':
                result = await resetUserCampaignMission(userId);
                done = `Chapter ${action.mission.number} timer restarted and goal progress wiped.`;
                break;
            case 'reopen':
                result = await reopenUserCampaignMission(userId, action.mission.mission_id);
                done = `Chapter ${action.mission.number} reopened as active (the reward it paid was not clawed back).`;
                break;
            case 'progress':
                result = await setUserCampaignGoalProgress(userId, action.goal.goal_id, { progressCurrent: action.value });
                done = `"${action.goal.title}" set to ${action.value} / ${action.goal.target}.`;
                break;
            case 'goalDone':
                result = await setUserCampaignGoalProgress(userId, action.goal.goal_id, { markCompleted: true });
                done = `"${action.goal.title}" marked completed.`;
                break;
        }
        setBusy(false);
        if (result.data == null) {
            setError(`Action failed: ${result.error ?? 'unknown error'}`);
            return;
        }
        setError(null);
        setData(result.data);
        setNotice(done);
    };

    const toggle = (missionId: number) => setCollapsed(prev => {
        const next = new Set(prev);
        if (next.has(missionId)) next.delete(missionId); else next.add(missionId);
        return next;
    });

    if (loading && data == null) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    if (data == null) return <Alert severity="error">{error ?? 'Failed to load the campaign.'}</Alert>;

    const tiers = data.star_tiers;
    return (
        <Stack gap={2}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="h6">Story progress: {data.completed_missions} / {data.total_missions}</Typography>
                <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                    <LinearProgress variant="determinate" value={data.total_missions === 0 ? 0 : 100 * data.completed_missions / data.total_missions} />
                </Box>
                <Stack direction="row" gap={1}>
                    {tiers.map(t => (
                        <Tooltip key={t.stars} title={`Completed within ${formatWithin(t.within_seconds)}`}>
                            <Chip size="small" variant="outlined" label={`${'★'.repeat(t.stars)} ×${t.reward_multiplier}`} />
                        </Tooltip>
                    ))}
                </Stack>
                {busy && <CircularProgress size={20} />}
            </Stack>

            {data.missions.map(mission => {
                const isOpen = !collapsed.has(mission.mission_id);
                const goalsDone = mission.goals.filter(g => g.status === 'COMPLETED').length;
                return (
                    <Accordion key={mission.mission_id} expanded={isOpen} onChange={() => toggle(mission.mission_id)} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" gap={2} sx={{ width: '100%', pr: 2 }} flexWrap="wrap">
                                <Typography sx={{ fontWeight: 600, minWidth: 260 }}>
                                    {mission.number}. {mission.subtitle}
                                    <Typography component="span" variant="body2" color="text.secondary"> · {mission.title}</Typography>
                                </Typography>
                                {stateChip(mission.state)}
                                {mission.state === 'COMPLETED' && <Stars value={mission.stars} />}
                                {mission.state === 'ACTIVE' && (
                                    <Tooltip title={`3★ until ${formatDate(mission.three_stars_until)} · 2★ until ${formatDate(mission.two_stars_until)}`}>
                                        <Stack direction="row" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" color="text.secondary">now:</Typography>
                                            <Stars value={mission.stars_if_completed_now} size={16} />
                                        </Stack>
                                    </Tooltip>
                                )}
                                {mission.state !== 'NEXT' && (
                                    <Typography variant="body2" color="text.secondary">goals {goalsDone} / {mission.goals.length}</Typography>
                                )}
                                <Box sx={{ flexGrow: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    reward {mission.base_reward.toLocaleString()}{mission.reward_paid != null && mission.reward_paid !== mission.base_reward ? ` → paid ${mission.reward_paid.toLocaleString()}` : ''}
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack gap={1.5}>
                                <Stack direction="row" gap={3} flexWrap="wrap">
                                    <Typography variant="body2">Started: <b>{formatDate(mission.started_at)}</b></Typography>
                                    <Typography variant="body2">Completed: <b>{formatDate(mission.completed_at)}</b></Typography>
                                    <Typography variant="body2">Time taken: <b>{formatDuration(mission.started_at, mission.completed_at ?? (mission.state === 'ACTIVE' ? new Date().toISOString() : null))}</b>{mission.state === 'ACTIVE' ? ' so far' : ''}</Typography>
                                    {mission.user_mission_id != null && <Typography variant="body2" color="text.secondary">row #{mission.user_mission_id}</Typography>}
                                </Stack>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Goal</TableCell>
                                            <TableCell>Task type</TableCell>
                                            <TableCell>Progress</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Skip</TableCell>
                                            <TableCell>Updated</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {mission.goals.map(goal => {
                                            const editable = mission.state === 'ACTIVE' && goal.user_task_id != null && goal.status !== 'COMPLETED';
                                            const draft = drafts[goal.goal_id] ?? String(goal.progress_current);
                                            return (
                                                <TableRow key={goal.goal_id}>
                                                    <TableCell>{goal.title}<Typography variant="caption" color="text.secondary"> #{goal.goal_id}</Typography></TableCell>
                                                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{goal.task_type ?? '—'}</Typography></TableCell>
                                                    <TableCell sx={{ minWidth: 160 }}>
                                                        <Stack direction="row" alignItems="center" gap={1}>
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <LinearProgress variant="determinate" value={Math.min(100, 100 * goal.progress_current / Math.max(1, goal.target))} />
                                                            </Box>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{goal.progress_current.toLocaleString()} / {goal.target.toLocaleString()}</Typography>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>{taskChip(goal)}</TableCell>
                                                    <TableCell>
                                                        {goal.was_skipped
                                                            ? <Tooltip title={`Paid ${goal.skip_price.toLocaleString()} betcoins to skip`}><Chip size="small" color="warning" label="skipped (paid)" /></Tooltip>
                                                            : <Typography variant="body2" color="text.secondary">{goal.skip_price.toLocaleString()}</Typography>}
                                                    </TableCell>
                                                    <TableCell>{formatDate(goal.updated_at)}</TableCell>
                                                    <TableCell align="right">
                                                        {editable && (
                                                            <Stack direction="row" gap={1} justifyContent="flex-end" alignItems="center">
                                                                <TextField
                                                                    size="small" type="number" value={draft} sx={{ width: 110 }}
                                                                    inputProps={{ min: 0, max: goal.target }}
                                                                    onChange={e => setDrafts(prev => ({ ...prev, [goal.goal_id]: e.target.value }))}
                                                                />
                                                                <Button size="small" variant="outlined" disabled={busy}
                                                                    onClick={() => setPending({ kind: 'progress', mission, goal, value: Math.max(0, Math.min(goal.target, parseInt(draft || '0', 10) || 0)) })}>
                                                                    Set
                                                                </Button>
                                                                <Button size="small" variant="contained" disabled={busy}
                                                                    onClick={() => setPending({ kind: 'goalDone', mission, goal })}>
                                                                    Mark done
                                                                </Button>
                                                            </Stack>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                <Stack direction="row" gap={1} justifyContent="flex-end" alignItems="center">
                                    {mission.can_complete && (
                                        <>
                                            <Select size="small" value={completeStars} onChange={e => setCompleteStars(Number(e.target.value))} sx={{ minWidth: 150 }}>
                                                {tiers.map(t => (
                                                    <MenuItem key={t.stars} value={t.stars}>
                                                        {'★'.repeat(t.stars)} — pays {Math.round(mission.base_reward * t.reward_multiplier).toLocaleString()}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <Button variant="contained" color="success" disabled={busy}
                                                onClick={() => setPending({ kind: 'complete', mission, stars: completeStars })}>
                                                Complete chapter
                                            </Button>
                                        </>
                                    )}
                                    {mission.can_reset && (
                                        <Button variant="outlined" color="warning" disabled={busy} onClick={() => setPending({ kind: 'reset', mission })}>
                                            Reset timer & progress
                                        </Button>
                                    )}
                                    {mission.can_reopen && (
                                        <Button variant="outlined" color="error" disabled={busy} onClick={() => setPending({ kind: 'reopen', mission })}>
                                            Reopen chapter
                                        </Button>
                                    )}
                                    {mission.state === 'COMPLETED' && !mission.can_reopen && (
                                        <Typography variant="caption" color="text.secondary">Only the last completed chapter can be reopened (the story is sequential).</Typography>
                                    )}
                                </Stack>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            <Dialog open={pending != null} onClose={() => setPending(null)}>
                <DialogTitle>Confirm</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pending?.kind === 'complete' && `Complete chapter ${pending.mission.number} "${pending.mission.subtitle}" with ${pending.stars} star(s)? Every goal will be marked done and ${Math.round(pending.mission.base_reward * (tiers.find(t => t.stars === pending.stars)?.reward_multiplier ?? 1)).toLocaleString()} betcoins paid.`}
                        {pending?.kind === 'reset' && `Restart chapter ${pending.mission.number}? The timer starts from now and every goal goes back to zero — including goals the player paid to skip (no refund).`}
                        {pending?.kind === 'reopen' && `Reopen chapter ${pending.mission.number}? It becomes active again with all goals at zero. The ${(pending.mission.reward_paid ?? pending.mission.base_reward).toLocaleString()} betcoins it paid are not clawed back.`}
                        {pending?.kind === 'progress' && `Set "${pending.goal.title}" to ${pending.value} / ${pending.goal.target}?`}
                        {pending?.kind === 'goalDone' && `Mark "${pending.goal.title}" as completed (not a paid skip)?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPending(null)}>Cancel</Button>
                    <Button variant="contained" onClick={runAction}>Confirm</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
